import React, { useEffect, useRef, useState } from "react";
import Bot from "./Bot";
import User from "./User";
import { getConversationId, sendFeedback } from "./utils";
import { CircularProgress } from "@mui/material";
import "./Chat.css";

const defaultMessage = {
  type: "bot",
  text: "Hi. I'm a special edition of Reece Chat, designed for you to test my ability to answer questions about the Leadership 3.0 framework. Let me know what you think by using the \"thumbs up\" or \"thumbs down\" options.",
};

const errorMessage = "I am sorry, **I am facing some issues**. Please try again.";

export const Chat = () => {
  const [inputText, setInputText] = useState("");
  const [conversationId, setConversationId] = useState("");
  const [messages, setMessages] = useState([defaultMessage]);
  const [isLoading, setIsLoading] = useState(false);
  const [waitingForStream, setWaitingForStream] = useState(false);
  const [intermediateSteps, setIntermediateSteps] = useState([]);
  const [showingIntermediateSteps, setShowingIntermediateSteps] = useState(false);
  const containerRef = useRef(null);
  const [feedbackMessageId, setFeedbackMessageId] = useState(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const ws = useRef(null);
  const streamStartTime = useRef(null);
  const minDisplayTime = 500; // Minimum time to show spinner in ms

  useEffect(() => {
    localStorage.setItem("conversation_type", "internal");
    getConversationId().then((id) => {
      setConversationId(id);
      setupWebSocket(id);
    });

    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, []);

  const hideWaitingSpinner = () => {
    if (!streamStartTime.current) {
      setWaitingForStream(false);
      return;
    }

    const elapsed = Date.now() - streamStartTime.current;
    const remaining = minDisplayTime - elapsed;

    if (remaining > 0) {
      setTimeout(() => setWaitingForStream(false), remaining);
    } else {
      setWaitingForStream(false);
    }
  };

  const setupWebSocket = (id) => {
    if (!id) return;

    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsProtocol}//${window.location.host}/internal/ws/${id}`;

    ws.current = new WebSocket(wsUrl);

    ws.current.onopen = () => {
      console.log("WebSocket connected");
    };

    ws.current.onmessage = (event) => {
      const data = event.data;

      if (data === "[END_OF_STREAM]") {
        setIsLoading(false);
        hideWaitingSpinner();
      } else if (data === "[INTERMEDIATE_STEPS_START]") {
        // Keep spinner visible - still thinking
        setIntermediateSteps([]);
        setShowingIntermediateSteps(true);
      } else if (data === "[INTERMEDIATE_STEPS_END]") {
        // Keep showing intermediate steps until final response starts
        // Keep spinner visible - still thinking
      } else if (data === "[FINAL_RESPONSE_START]") {
        hideWaitingSpinner();
        // Clear intermediate steps and start final response
        setIntermediateSteps([]);
        setShowingIntermediateSteps(false);
        // Create new bot message for final response
        setMessages((prevMessages) => [...prevMessages, { type: "bot", text: "" }]);
      } else if (data.startsWith("[INTERMEDIATE_STEP]")) {
        // Keep spinner visible - still thinking
        // Add intermediate step
        const stepText = data.replace("[INTERMEDIATE_STEP]", "");
        setIntermediateSteps((prevSteps) => [...prevSteps, stepText]);
        scrollToBottom();
      } else {
        hideWaitingSpinner();
        // Regular message chunk - actual content starting
        setMessages((prevMessages) => {
          const lastMessage = prevMessages[prevMessages.length - 1];
          if (lastMessage && lastMessage.type === "bot") {
            // Append the new chunk to the last bot message
            const updatedMessages = [...prevMessages];
            updatedMessages[prevMessages.length - 1] = {
              ...lastMessage,
              text: lastMessage.text + data,
            };
            return updatedMessages;
          } else {
            // Or create a new bot message
            return [...prevMessages, { type: "bot", text: data }];
          }
        });
      }
    };

    ws.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setIsLoading(false);
      hideWaitingSpinner();
    };

    ws.current.onclose = () => {
      console.log("WebSocket disconnected");
      setIsLoading(false);
      hideWaitingSpinner();
    };
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      const container = containerRef.current;
      container.scrollTop = container.scrollHeight;
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLoading || !inputText.trim()) return;

    setIsLoading(true);
    setWaitingForStream(true);
    streamStartTime.current = Date.now();
    // Clear any existing intermediate steps when starting a new query
    setIntermediateSteps([]);
    setShowingIntermediateSteps(false);
    setMessages((prev) => [...prev, { type: "user", text: inputText }]);
    setInputText("");
    scrollToBottom();

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(inputText);
    } else {
      console.error("WebSocket is not connected.");
      // Handle the case where the WebSocket is not ready, e.g., show an error message.
      setMessages((prev) => [...prev, { type: "bot", text: errorMessage }]);
      setIsLoading(false);
      hideWaitingSpinner();
    }
  };

  const handleFeedbackSubmit = async (messageId, feedbackData) => {
    setSubmittingFeedback(true);
    try {
      await sendFeedback(
        conversationId,
        feedbackData.feedbackText,
        {
          category: feedbackData.category,
          severity: feedbackData.severity,
          user_email: feedbackData.email,
          message_id: messageId
        }
      );
      setFeedbackMessage({ type: "success", text: "Thank you for your feedback!" });
      setFeedbackMessageId(null);
    } catch (error) {
      setFeedbackMessage({ type: "error", text: error.message });
    } finally {
      setSubmittingFeedback(false);
      setTimeout(() => setFeedbackMessage(null), 5000);
    }
  };

  return (
    <div className="chat">
      <div className="messages" ref={containerRef}>
        {messages.map((message, index) =>
          message.type === "bot" ? (
            <Bot
              key={index}
              botText={message.text}
              loading={isLoading && index === messages.length - 1}
              conversationId={conversationId}
              showFeedback={index !== 0}
              onFeedbackSubmit={(feedbackData) => handleFeedbackSubmit(index, feedbackData)}
              isSubmittingFeedback={submittingFeedback && feedbackMessageId === index}
              feedbackMessage={feedbackMessageId === index ? feedbackMessage : null}
              messageId={index}
            />
          ) : (
            <User key={index} userText={message.text} />
          )
        )}

        {/* Loading spinner when waiting for stream to start */}
        {waitingForStream && (
          <div className="loading-container">
            <div className="loading-message">
              <img className="bot-logo" src={`${process.env.PUBLIC_URL}/assets/bot.png`} alt="bot" />
              <div className="loading-content">
                <CircularProgress size={16} className="loading-spinner" />
                <span className="loading-text">Reece Chat is thinking...</span>
              </div>
            </div>
          </div>
        )}

        {/* Display intermediate steps */}
        {showingIntermediateSteps && intermediateSteps.length > 0 && !waitingForStream && (
          <div className="intermediate-steps-container">
            <div className="intermediate-steps-header">
              <span className="thinking-indicator">🤔 Processing your water heater query...</span>
            </div>
            {intermediateSteps.map((step, index) => (
              <div key={index} className="intermediate-step">
                {step}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="input-container">
        <form onSubmit={handleSubmit} className="input-form">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Ask Reece Chat..."
            className="message-input"
          />
          <button
            type="submit"
            className="send-button"
            disabled={isLoading}
            aria-label="Send"
          >
            ➤
          </button>
        </form>
      </div>
    </div>
  );
};


